import { ActionCreator } from 'react-redux'
import { ThunkAction } from 'redux-thunk'

import { IRootState } from '../../common/reducers'
import { getConfigBySite } from '../../config/sites'
import menusConfigs from '../../config/sites/menus'
import experimentalMenusConfigs from '../../config/sites/menus/experimentalMenusConfigs'
import { AlertType, IAlertPayload } from '../../typings/alert'
import { IPageSeoMeta } from '../../typings/seoMetadata'
import { reportProductsImpressions } from '../analytics/resultAnalytics'
import { Site } from '../constants/siteList'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { logError } from '../utils/errors'
import { getItemRankOffset } from '../utils/item'
import {
  isHomePageLocation,
  isSearchPageLocation
} from '../utils/locationHelper'
import { getTopLevelRoute } from '../utils/urlHelper'
import { setExperiments } from './experiments'

export enum TypeKeys {
  GET_STATS_SUCCESS = 'GET_STATS_SUCCESS',
  GET_STATS_FAIL = 'GET_STATS_FAIL',
  SET_CURRENT_SITE = 'SET_CURRENT_SITE',
  SET_BASE_URL = 'SET_BASE_URL',
  SET_MENU_CONFIG = 'SET_MENU_CONFIG',
  SHOW_ALERT = 'SHOW_ALERT',
  HIDE_ALERT = 'HIDE_ALERT',
  RESET_ALERT = 'RESET_ALERT',
  SET_ERROR_PAGE = 'SET_ERROR_PAGE',
  RESET_ERROR_PAGE = 'RESET_ERROR_PAGE',
  NO_INDEX = 'NO_INDEX',
  SET_CLIENT_VERSION = 'SET_CLIENT_VERSION',
  SET_CLIENT_FINISHED_LOADING = 'SET_CLIENT_FINISHED_LOADING',
  SET_PRODUCT_IMPRESSIONS = 'SET_PRODUCT_IMPRESSIONS',
  SET_SEO_META = 'SET_SEO_META',
  SET_CANONICAL_PATH = 'SET_CANONICAL_PATH'
}

export interface IStatsData {
  total_items_sold: number
  total_community_earnings: number
}

export interface IGetStatsSuccessAction {
  type: TypeKeys.GET_STATS_SUCCESS
  payload: Readonly<IStatsData>
}

export interface IGetStatsFailAction {
  type: TypeKeys.GET_STATS_FAIL
  error: boolean
}

export interface ISetCurrentSiteAction {
  type: TypeKeys.SET_CURRENT_SITE
  payload: Site
}

export interface ISetBaseUrlAction {
  type: TypeKeys.SET_BASE_URL
  payload: string
}

export interface ISetMenuConfigAction {
  type: TypeKeys.SET_MENU_CONFIG
  payload: any[]
}

export interface IShowAlertAction {
  type: TypeKeys.SHOW_ALERT
  payload: Readonly<IAlertPayload>
}

export interface IHideAlertAction {
  type: TypeKeys.HIDE_ALERT
  payload: Readonly<{ message: string }>
}

export interface IResetAlertAction {
  type: TypeKeys.RESET_ALERT
}

export interface ISetErrorPage {
  type: TypeKeys.SET_ERROR_PAGE
  payload: Readonly<{ id: any }>
}

export interface IResetErrorPage {
  type: TypeKeys.RESET_ERROR_PAGE
}

export interface INoIndexAction {
  type: TypeKeys.NO_INDEX
}

export interface ISetClientVersionAction {
  type: TypeKeys.SET_CLIENT_VERSION
  payload: Readonly<{ clientVersion: number }>
}

export interface ISetClientFinishedLoadingAction {
  type: TypeKeys.SET_CLIENT_FINISHED_LOADING
}

export interface ISetSeoMetaAction {
  type: TypeKeys.SET_SEO_META
  payload: Readonly<IPageSeoMeta>
}

export interface ISetCanonicalPathAction {
  type: TypeKeys.SET_CANONICAL_PATH
  payload: Readonly<string>
}

export interface ISetProductImpressionsAction {
  type: TypeKeys.SET_PRODUCT_IMPRESSIONS
}

export type ActionTypes =
  | IGetStatsSuccessAction
  | IGetStatsFailAction
  | ISetCurrentSiteAction
  | ISetBaseUrlAction
  | ISetMenuConfigAction
  | IShowAlertAction
  | IHideAlertAction
  | IResetAlertAction
  | ISetErrorPage
  | IResetErrorPage
  | INoIndexAction
  | ISetClientVersionAction
  | ISetClientFinishedLoadingAction
  | ISetCanonicalPathAction
  | ISetProductImpressionsAction
  | ISetSeoMetaAction

let CACHED_STATS: IStatsData | null = null

export const fetchStats: ActionCreator<
  ThunkAction<
    Promise<IGetStatsSuccessAction | IGetStatsFailAction>,
    IRootState,
    void
  >
> = ({ apiUrlPrefix }: { apiUrlPrefix: string }) => {
  return (dispatch, getState) => {
    const state = getState()
    if (state.common.stats) {
      const action: IGetStatsSuccessAction = {
        type: TypeKeys.GET_STATS_SUCCESS,
        payload: state.common.stats
      }
      return Promise.resolve(dispatch(action))
    }

    if (CACHED_STATS) {
      const action: IGetStatsSuccessAction = {
        type: TypeKeys.GET_STATS_SUCCESS,
        payload: CACHED_STATS
      }
      return Promise.resolve(dispatch(action))
    }

    return axiosVerbose
      .get<IStatsData>(`${apiUrlPrefix}/shopfront_api/stat`)
      .then(response => {
        CACHED_STATS = response.data

        const action: IGetStatsSuccessAction = {
          type: TypeKeys.GET_STATS_SUCCESS,
          payload: CACHED_STATS
        }

        return dispatch(action)
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        const action: IGetStatsFailAction = {
          type: TypeKeys.GET_STATS_FAIL,
          error: true
        }
        return dispatch(action)
      })
  }
}

export const setCurrentSite: ActionCreator<
  ThunkAction<ISetCurrentSiteAction, IRootState, void>
> = (currentSite: Site) => {
  return (dispatch, getState) => {
    dispatch({
      type: 'loadSiteConfigs',
      payload: {
        currentSite
      }
    })

    const action: ISetCurrentSiteAction = {
      type: TypeKeys.SET_CURRENT_SITE,
      payload: currentSite
    }
    return dispatch(action)
  }
}

export const setBaseUrl = (baseUrl: string) => {
  const action: ISetBaseUrlAction = {
    type: TypeKeys.SET_BASE_URL,
    payload: baseUrl
  }
  return action
}

export const setMenuConfig = (currentSite: Site) => {
  const action: ISetMenuConfigAction = {
    type: TypeKeys.SET_MENU_CONFIG,
    payload: menusConfigs[currentSite] || []
  }
  return action
}

export const setExperimentalMenuConfig = (currentSite: Site) => {
  const action: ISetMenuConfigAction = {
    type: TypeKeys.SET_MENU_CONFIG,
    payload: experimentalMenusConfigs[currentSite] || []
  }
  return action
}

export const showAlert = (
  message: string,
  type: AlertType = AlertType.warning,
  canBeDismissed = true
) => {
  const action: IShowAlertAction = {
    type: TypeKeys.SHOW_ALERT,
    payload: {
      message,
      type,
      canBeDismissed
    }
  }
  return action
}

export const hideAlert = (message: string) => {
  const action: IHideAlertAction = {
    type: TypeKeys.HIDE_ALERT,
    payload: {
      message
    }
  }
  return action
}

export const resetAlerts = () => {
  const action: IResetAlertAction = {
    type: TypeKeys.RESET_ALERT
  }
  return action
}

export const setErrorPage = (id: any) => {
  const action: ISetErrorPage = {
    type: TypeKeys.SET_ERROR_PAGE,
    payload: {
      id
    }
  }
  return action
}

export const resetErrorPage = () => {
  const action: IResetErrorPage = {
    type: TypeKeys.RESET_ERROR_PAGE
  }
  return action
}

export const noIndex = () => {
  const action: INoIndexAction = {
    type: TypeKeys.NO_INDEX
  }
  return action
}

export const setClientVersion = ({
  clientVersion
}: {
  clientVersion: number
}) => {
  const action: ISetClientVersionAction = {
    type: TypeKeys.SET_CLIENT_VERSION,
    payload: { clientVersion }
  }
  return action
}

export const setProductImpressions = (): ThunkAction<
  void,
  IRootState,
  void
> => {
  return (dispatch, getState) => {
    const items = getState().searchPage.results.matches
    const { featuredItems, freeItems } = getState().homePage
    const currentSite = getState().common.currentSite
    const currentLocation = getState().router.location
    const currentPage = Number(currentLocation.query.page) || 0
    const itemRankOffset = getItemRankOffset(currentSite, currentPage)

    if (isSearchPageLocation(currentLocation.pathname)) {
      reportProductsImpressions({
        items,
        currentSite,
        itemRankOffset,
        page: 'Search Results',
        section: getTopLevelRoute(currentLocation.pathname)
      })
    }

    if (isHomePageLocation(currentLocation.pathname)) {
      if (featuredItems) {
        reportProductsImpressions({
          items: featuredItems.matches.slice(0, 10),
          currentSite,
          itemRankOffset: 1,
          page: 'Homepage',
          section: 'Featured items'
        })
      }

      if (freeItems) {
        reportProductsImpressions({
          items: freeItems.matches.slice(0, 6),
          currentSite,
          itemRankOffset: 1,
          page: 'Homepage',
          section: 'Free files of the month'
        })
      }
    }

    dispatch({
      type: TypeKeys.SET_PRODUCT_IMPRESSIONS
    })
  }
}

export const clientFinishedLoading = (): ThunkAction<any, IRootState, void> => {
  return (dispatch, getState) => {
    dispatch(setExperiments())
    dispatch(setProductImpressions())
  }
}

export const setCanonicalPath = (path: string) => {
  const action: ISetCanonicalPathAction = {
    type: TypeKeys.SET_CANONICAL_PATH,
    payload: path
  }
  return action
}

export const setSeoMeta = ({
  title,
  meta_description,
  description
}: {
  title: string
  meta_description?: string
  description: string
}) => {
  const action: ISetSeoMetaAction = {
    type: TypeKeys.SET_SEO_META,
    payload: {
      title,
      meta_description: meta_description || description
    }
  }
  return action
}
